import { useCallback, useEffect, useRef, useState } from 'react';
import { BridgeWebEventType, type SessionResponsePayload } from '@pintrack/bridge';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/lib/auth/AuthContext';
import { bridgeRequest, inNativeShell } from '@/lib/bridge/bridgeClient';
import { googleConfigured, initGoogleIdentity } from '@/lib/auth/googleIdentity';
import { errorMessage } from '@/lib/api/errors';
import { intlTag, t } from '@/lib/i18n';
import { authStyles } from './AuthLayout';

/**
 * Social sign-in.
 *
 * O Google é o botão **do Google**, renderizado pelo próprio script: as diretrizes de marca
 * dele exigem o logotipo e o rótulo oficiais, e desenhar uma cápsula nossa com a letra G seria
 * infringi-las. `shape: 'pill'` e `size: 'large'` aproximam do sistema visual da tela sem
 * mexer no que não é nosso.
 *
 * A Apple continua desligada: as chaves de servidor (`APPLE_*`) estão vazias, então o botão
 * fica no mesmo estado "existe, bloqueado" que o canvas usa em 1m para Alterar senha.
 *
 * ## Dentro da casca nativa é outro caminho
 *
 * O script do Google **não roda em WebView embutido**: o endpoint de autorização responde
 * `disallowed_useragent` desde julho de 2023, por política, e nenhum user-agent contorna. Então
 * na casca o botão do Google é nosso mesmo — não há botão dele para desenhar — e quem faz o
 * sign-in é o lado nativo, que devolve o `idToken` pela ponte. O `loginWithGoogle` do
 * `AuthContext` é o mesmo nos dois casos: ele sempre recebeu um token cru e nunca soube de onde
 * veio.
 */
export function OAuthButtons() {
  const { loginWithGoogle } = useAuth();
  const slot = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [nativeBusy, setNativeBusy] = useState(false);

  const signInNatively = useCallback(async () => {
    setError(null);
    setNativeBusy(true);
    try {
      const reply = await bridgeRequest<SessionResponsePayload>(BridgeWebEventType.Session, {
        interactive: true,
      });
      // `null` só aconteceria fora da casca, e aqui já sabemos que estamos nela.
      if (!reply) return;
      // Cancelar o seletor de contas volta `ok: false` sem `error` — não é falha para mostrar.
      if (!reply.ok) {
        if (reply.error) setError(reply.error);
        return;
      }
      if (!reply.payload?.idToken) {
        setError(t('auth.oauth.notConfigured'));
        return;
      }
      await loginWithGoogle(reply.payload.idToken);
    } catch (e: unknown) {
      setError(errorMessage(e));
    } finally {
      setNativeBusy(false);
    }
  }, [loginWithGoogle]);

  useEffect(() => {
    if (!googleConfigured || inNativeShell) return;
    let cancelled = false;

    void initGoogleIdentity((credential) => {
      setError(null);
      loginWithGoogle(credential).catch((e: unknown) => setError(errorMessage(e)));
    })
      .then((api) => {
        if (cancelled || !slot.current) return;
        // Redesenhar sobre o mesmo nó empilharia dois botões quando o efeito roda de novo.
        slot.current.replaceChildren();
        api.renderButton(slot.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          shape: 'pill',
          text: 'continue_with',
          locale: intlTag,
          width: slot.current.clientWidth || undefined,
        });
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(errorMessage(e));
      });

    return () => {
      cancelled = true;
    };
  }, [loginWithGoogle]);

  return (
    <>
      <div className={authStyles.divider}>
        <span className={authStyles.dividerLine} />
        <span className={authStyles.dividerLabel}>{t('common.or')}</span>
        <span className={authStyles.dividerLine} />
      </div>

      <div className={authStyles.oauth}>
        {inNativeShell ? (
          <Button
            variant="secondary"
            block
            disabled={nativeBusy}
            onClick={() => void signInNatively()}
          >
            {t('auth.oauth.google')}
          </Button>
        ) : googleConfigured ? (
          <div ref={slot} className={authStyles.googleSlot} />
        ) : (
          <Button variant="secondary" block disabled>
            {t('auth.oauth.google')}
          </Button>
        )}
        <Button variant="secondary" block disabled>
          {t('auth.oauth.apple')}
        </Button>
      </div>

      {error && <p className={authStyles.oauthError}>{error}</p>}

      {/*
        Na casca o `VITE_GOOGLE_CLIENT_ID` é irrelevante — quem assina é o cliente nativo, com o
        client ID do app. Mostrar "não configurado" ali seria informação falsa sobre um botão que
        funciona.
      */}
      {!googleConfigured && !inNativeShell && (
        <p className={authStyles.oauthNote}>
          {t('auth.oauth.notConfigured')}
        </p>
      )}
      {googleConfigured && (
        <p className={authStyles.oauthNote}>
          {t('auth.oauth.appleNotConfigured')}
        </p>
      )}
    </>
  );
}
